import { keepPreviousData } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import {
  LogisticsIncoterm,
  type MsrIncoTermsQuery,
  type MsrUnitTypesQuery,
} from '~/graphql/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';

const orderMsrPurchaseOrderQuery = graphql(`
  query GetOrderMsrPurchaseOrder($orderId: ID!) {
    order(id: $orderId) {
      id
      msrPurchaseOrder {
        fields {
          key
          value
          editability
        }
        items {
          fields {
            key
            value
            editability
          }
        }
      }
    }
  }
`);

/**
 * Runs only once MSR's purchase order has been fetched, and refetches when
 * `msrSyncedAt` moves, while the previous snapshot stays on screen.
 */
export const useGetOrderMsrPurchaseOrder = (
  orderId: string,
  msrSyncedAt: string | null | undefined
) =>
  useGraphqlQuery(
    orderMsrPurchaseOrderQuery,
    ['msrPurchaseOrder', orderId, msrSyncedAt],
    { orderId },
    { enabled: msrSyncedAt != null, placeholderData: keepPreviousData }
  );

const msrUnitTypesQuery = graphql(`
  query MsrUnitTypes($transportVehicleId: ID!) {
    msrUnitTypes(transportVehicleId: $transportVehicleId) {
      code
      description
      isDefault
    }
  }
`);

export type MsrUnitTypeOption = MsrUnitTypesQuery['msrUnitTypes'][number];

export const useMsrUnitTypes = (
  transportVehicleId: string | null | undefined,
  enabled: boolean
) => {
  const query = useGraphqlQuery(
    msrUnitTypesQuery,
    ['msrUnitTypes', transportVehicleId],
    { transportVehicleId: transportVehicleId ?? '' },
    { enabled: enabled && !!transportVehicleId, staleTime: Infinity }
  );
  return {
    unitTypes: query.data?.msrUnitTypes ?? [],
    isLoading: query.isLoading,
  };
};

const msrIncoTermsQuery = graphql(`
  query MsrIncoTerms(
    $incoTerm: LogisticsIncoterm!
    $incotermDischarge: LogisticsIncoterm
  ) {
    msrIncoTerms(incoTerm: $incoTerm, incotermDischarge: $incotermDischarge) {
      code
      isDefault
      defaultLocation
    }
  }
`);

export type MsrIncoTermOption = MsrIncoTermsQuery['msrIncoTerms'][number];

export const useMsrIncoTerms = (
  incoTerm: LogisticsIncoterm | null,
  incotermDischarge: LogisticsIncoterm | null,
  enabled: boolean
) => {
  const query = useGraphqlQuery(
    msrIncoTermsQuery,
    ['msrIncoTerms', incoTerm, incotermDischarge],
    { incoTerm: incoTerm ?? LogisticsIncoterm.Exw, incotermDischarge },
    { enabled: enabled && incoTerm != null, staleTime: Infinity }
  );
  return {
    incoTerms: query.data?.msrIncoTerms ?? [],
    isLoading: query.isLoading,
  };
};
